import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { fullWidth } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { selectPartsData, setIsEditModeForPartSwitchOn } from '@reducers/details';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  IMAGES_PANEL_VIEW_MODE_WIDTH,
  PANEL_VIEW_MODE_WIDTH,
} from '../../../../../../common/constants/base.constants';
import type { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import type { PartSearchItem } from '@interfaces/search';
import type { PartListOrSingleItemProps } from '@v2/pages/machines/modules/now/components/Part/components/PartListOrSingleItem';

export interface UseManagePartListOrSingleItemParams extends Pick<PartListOrSingleItemProps, 'partId' | 'displaySinglePart'> {}

export const useManagePartListOrSingleItem = (params: UseManagePartListOrSingleItemParams) => {
  const { partId, displaySinglePart } = params;

  const dispatch = useAppDispatch();
  const parts = useSelector(selectPartsData) as ReducerEntityWithLoadingState<PartSearchItem[]>;

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const shouldDisplaySinglePart = displaySinglePart || parts?.data?.length === 1;

  useFocusEffect(
    useCallback(() => {
      return () => {
        dispatch(setIsEditModeForPartSwitchOn(false));
      };
    }, [])
  );

  const onPartImagePress = (selectedImageKey: string) => {
    navigateToMainDetailsScreen({
      mainScreen: PartDetailsScreenName,
      subScreen: PartDetailsScreen.PART,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.PART,
        partId,
        partName: parts?.data?.[0]?.partName,
        selectedImageKey,
      },
    });
  };

  const maxWidth = shouldDisplaySinglePart ? PANEL_VIEW_MODE_WIDTH : fullWidth;
  const maxDetailsWidth = shouldDisplaySinglePart ? PANEL_VIEW_MODE_WIDTH - IMAGES_PANEL_VIEW_MODE_WIDTH : fullWidth;
  const bg = !isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field';
  const showImages = isPhone && shouldDisplaySinglePart;
  const showSideImagesContainer = parts.wasLoaded && !isPhone && shouldDisplaySinglePart;

  return {
    parts,
    onPartImagePress,
    maxWidth,
    maxDetailsWidth,
    bg,
    showImages,
    showSideImagesContainer,
  };
};
